'use client';

import { c as _c } from "react/compiler-runtime";
import OptimizedImage from '@/components/OptimizedImage';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useParams } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { useEventListener } from 'usehooks-ts';
import IconClose from '@/components/assets/icon_close.svg';
import IconFullscreen from '@/components/assets/icon_fullscreen.svg';
import IconPause from '@/components/assets/icon_pause.svg';
import IconPicture from '@/components/assets/icon_picture.svg';
import IconPlay from '@/components/assets/icon_play.svg';
import IconVideo from '@/components/assets/icon_video.svg';
import IconVolumeEnable from '@/components/assets/icon_volume_enable.svg';
import ViverseVideoPlayer from '@/components/ViverseVideoPlayer';
import { useBlendVisionSources } from '@/hooks/useBlendVisionSources';
import { buildPublicProfileShareVideoUrl } from '@/lib/share-link';
import { cn } from '@/lib/utils';
import { Asset } from '@/types/asset';
type LightboxProps = {
  assets: Asset[];
  selectedAssetIndex: number;
  setIsLightboxOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedAssetIndex: React.Dispatch<React.SetStateAction<number>>;
  share?: boolean;
};
type MainLightboxContentProps = {
  asset: Asset;
  isPlaying: boolean;
  setIsPlaying: React.Dispatch<React.SetStateAction<boolean>>;
  videoRef: React.RefObject<HTMLVideoElement | null>;
  handleClickPlay: () => void;
  share?: boolean;
};
type NavigationButtonsProps = {
  assets: Asset[];
  selectedAssetIndex: number;
  setSelectedAssetIndex: React.Dispatch<React.SetStateAction<number>>;
};
type ThumbnailListProps = {
  assets: Asset[];
  selectedAssetIndex: number;
  setSelectedAssetIndex: React.Dispatch<React.SetStateAction<number>>;
};
function LightboxBackground(t0) {
  const $ = _c(7);
  const {
    onClick,
    children
  } = t0;
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      opacity: 0
    };
    t2 = {
      opacity: 1
    };
    t3 = {
      opacity: 0
    };
    t4 = {
      duration: 0.3
    };
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
    $[3] = t4;
  } else {
    t1 = $[0];
    t2 = $[1];
    t3 = $[2];
    t4 = $[3];
  }
  let t5;
  if ($[4] !== children || $[5] !== onClick) {
    t5 = <motion.div className="z-overlay fixed inset-0 flex h-full w-full flex-col items-center justify-center bg-[#131B26E5] backdrop-blur-md" onClick={onClick} initial={t1} animate={t2} exit={t3} transition={t4}>{children}</motion.div>;
    $[4] = children;
    $[5] = onClick;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  return t5;
}
function MainLightboxContent(t0) {
  const $ = _c(39);
  const {
    asset,
    isPlaying,
    setIsPlaying,
    videoRef,
    handleClickPlay,
    share: t1
  } = t0;
  const share = t1 === undefined ? true : t1;
  const [volume, setVolume] = useState(1);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const params = useParams();
  const identifier = params.identifier;
  let t2;
  if ($[0] !== isPlaying || $[1] !== setIsPlaying || $[2] !== videoRef) {
    t2 = () => {
      const video = videoRef.current;
      if (!video) {
        return;
      }
      if (isPlaying) {
        video.pause();
      } else {
        video.play();
      }
      setIsPlaying(!isPlaying);
    };
    $[0] = isPlaying;
    $[1] = setIsPlaying;
    $[2] = videoRef;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const togglePlay = t2;
  let t3;
  if ($[4] !== videoRef) {
    t3 = () => {
      const video_0 = videoRef.current;
      if (video_0) {
        setCurrentTime(video_0.currentTime);
      }
    };
    $[4] = videoRef;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const handleTimeUpdate = t3;
  let t4;
  if ($[6] !== videoRef) {
    t4 = () => {
      const video_1 = videoRef.current;
      if (video_1) {
        setDuration(video_1.duration);
      }
    };
    $[6] = videoRef;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const handleLoadedMetadata = t4;
  let t5;
  if ($[8] !== videoRef) {
    t5 = e => {
      const video_2 = videoRef.current;
      const time = parseFloat(e.target.value);
      if (video_2) {
        video_2.currentTime = time;
      }
      setCurrentTime(time);
    };
    $[8] = videoRef;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const handleProgressChange = t5;
  const formatTime = _temp;
  let t6;
  if ($[10] !== videoRef) {
    t6 = () => {
      const video_3 = videoRef.current;
      if (video_3) {
        if (document.fullscreenElement) {
          document.exitFullscreen();
        } else {
          video_3.requestFullscreen();
        }
      }
    };
    $[10] = videoRef;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const toggleFullscreen = t6;
  const memoizedVideoSources = useBlendVisionSources(asset.video);
  const t7 = asset.image?.url || asset.video?.url;
  let t10;
  let t11;
  let t8;
  let t9;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      opacity: 0
    };
    t9 = {
      opacity: 1
    };
    t10 = {
      opacity: 0
    };
    t11 = {
      duration: 0.3
    };
    $[12] = t10;
    $[13] = t11;
    $[14] = t8;
    $[15] = t9;
  } else {
    t10 = $[12];
    t11 = $[13];
    t8 = $[14];
    t9 = $[15];
  }
  let t12;
  if ($[16] !== asset.image || $[17] !== asset.video?.playbackToken || $[18] !== asset.video?.title || $[19] !== asset.video?.url || $[20] !== asset.video?.vodId || $[21] !== currentTime || $[22] !== duration || $[23] !== handleClickPlay || $[24] !== handleLoadedMetadata || $[25] !== handleProgressChange || $[26] !== handleTimeUpdate || $[27] !== identifier || $[28] !== isPlaying || $[29] !== memoizedVideoSources || $[30] !== share || $[31] !== toggleFullscreen || $[32] !== togglePlay || $[33] !== videoRef || $[34] !== volume) {
    t12 = asset.image?.url ? <OptimizedImage src={asset.image.url} className="mx-auto block size-full w-fit object-contain" alt="cover" fill={true} sizes="(max-width: 768px) 100vw, (max-width: 1280px) 900px, 1200px" /> : asset.video?.playbackToken && memoizedVideoSources ? <div className="size-full rounded-none bg-gray-500/10 object-cover"><ViverseVideoPlayer title={asset.video?.title} playbackState="playing" source={memoizedVideoSources} share={share ? {
        url: buildPublicProfileShareVideoUrl(identifier, asset.video?.vodId)
      } : undefined} /></div> : asset.video?.url && <><video ref={videoRef} src={asset.video?.url} onClick={togglePlay} onTimeUpdate={handleTimeUpdate} onLoadedMetadata={handleLoadedMetadata} className="mx-auto block size-full rounded-none bg-gray-500/10 object-cover" /><div className="absolute bottom-0 left-1/2 flex w-[85%] -translate-x-1/2 flex-col items-center justify-center space-y-2 md:w-[65%] md:space-y-6 lg:w-[95%]"><input type="range" className="range-no-thumb z-floating h-1.5 w-full cursor-pointer appearance-none" min={0} max={duration} value={currentTime} onChange={handleProgressChange} style={{
          background: `linear-gradient(to right, #0589C9 ${currentTime / duration * 100}%, #D1D5DB ${currentTime / duration * 100}%)`
        }} /><div className="flex h-[34px] w-full items-center justify-between md:h-14"><div className="flex items-center justify-center space-x-4"><div className="flex h-[34px] w-[34px] items-center justify-center md:h-14 md:w-14" onClick={togglePlay}>{isPlaying ? <IconPause className="size-5 cursor-pointer text-white md:size-7" /> : <IconPlay className="fill-vive-primary-20 size-5 cursor-pointer md:size-7" />}</div><div onClick={() => setVolume(volume === 0 ? 1 : 0)} className="flex h-[34px] w-[34px] cursor-pointer items-center justify-center md:h-14 md:w-14">{volume === 0 ? <IconVolumeEnable className="size-5 opacity-25 md:size-7" /> : <IconVolumeEnable className="size-5 md:size-7" />}</div><span>{formatTime(currentTime)} / {formatTime(duration)}</span></div><div className="flex items-center space-x-3"><div className="relative cursor-pointer"><IconFullscreen onClick={toggleFullscreen} className="size-5 md:size-7" /></div></div></div></div>{!isPlaying && <div onClick={handleClickPlay} className="cursor-pointer"><div className="bg-primary absolute top-1/2 left-1/2 flex h-[54px] w-[54px] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full md:h-16 md:w-16 lg:h-20 lg:w-20"><IconPlay className="fill-vive-primary-20 size-7 md:size-8 lg:size-10" /></div></div>}</>;
    $[16] = asset.image;
    $[17] = asset.video?.playbackToken;
    $[18] = asset.video?.title;
    $[19] = asset.video?.url;
    $[20] = asset.video?.vodId;
    $[21] = currentTime;
    $[22] = duration;
    $[23] = handleClickPlay;
    $[24] = handleLoadedMetadata;
    $[25] = handleProgressChange;
    $[26] = handleTimeUpdate;
    $[27] = identifier;
    $[28] = isPlaying;
    $[29] = memoizedVideoSources;
    $[30] = share;
    $[31] = toggleFullscreen;
    $[32] = togglePlay;
    $[33] = videoRef;
    $[34] = volume;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  let t13;
  if ($[36] !== t12 || $[37] !== t7) {
    t13 = <motion.div key={t7} className="3xl:max-w-[1138px] relative m-auto flex aspect-[390/219] w-full items-center justify-center md:aspect-[431/242] md:max-w-[431px] lg:aspect-[1138/640] lg:max-w-[900px] lg:rounded-[15px]" initial={t8} animate={t9} exit={t10} transition={t11}>{t12}</motion.div>;
    $[36] = t12;
    $[37] = t7;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  return t13;
}
function _temp(time_0) {
  const minutes = Math.floor(time_0 / 60);
  const seconds = Math.floor(time_0 % 60);
  return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
}
function NavigationButtons(t0) {
  const $ = _c(22);
  const {
    assets,
    selectedAssetIndex,
    setSelectedAssetIndex
  } = t0;
  let t1;
  if ($[0] !== selectedAssetIndex || $[1] !== setSelectedAssetIndex) {
    t1 = () => {
      if (selectedAssetIndex > 0) {
        setSelectedAssetIndex(_temp2);
      }
    };
    $[0] = selectedAssetIndex;
    $[1] = setSelectedAssetIndex;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const handleLeftClick = t1;
  let t2;
  if ($[3] !== assets.length || $[4] !== selectedAssetIndex || $[5] !== setSelectedAssetIndex) {
    t2 = () => {
      if (selectedAssetIndex < assets.length - 1) {
        setSelectedAssetIndex(_temp3);
      }
    };
    $[3] = assets.length;
    $[4] = selectedAssetIndex;
    $[5] = setSelectedAssetIndex;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const handleRightClick = t2;
  const t3 = selectedAssetIndex > 0;
  let t4;
  if ($[7] !== t3) {
    t4 = cn("group z-interactive absolute top-1/2 size-8 translate-x-5 -translate-y-1/2 cursor-pointer place-items-center rounded-full bg-[#59667799] opacity-0 transition-all duration-300 hover:bg-[#6E7C8ECC] md:grid md:size-[52px] md:-translate-x-10 lg:-translate-x-20", {
      "opacity-100": t3
    });
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <ChevronLeft className="stroke-vive-primary-30 group-hover:stroke-vive-primary-0 size-[36px] transition-all" />;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== handleLeftClick || $[11] !== t4) {
    t6 = <div className={t4} onClick={handleLeftClick}>{t5}</div>;
    $[10] = handleLeftClick;
    $[11] = t4;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const t7 = selectedAssetIndex < assets?.length - 1;
  let t8;
  if ($[13] !== t7) {
    t8 = cn("group z-interactive absolute top-1/2 right-0 size-8 -translate-x-5 -translate-y-1/2 cursor-pointer place-items-center rounded-full bg-[#59667799] opacity-0 transition-all duration-300 hover:bg-[#6E7C8ECC] md:grid md:size-[52px] md:translate-x-10 lg:translate-x-20", {
      "opacity-100": t7
    });
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <ChevronRight className="stroke-vive-primary-30 group-hover:stroke-vive-primary-0 size-[36px] transition-all" />;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== handleRightClick || $[17] !== t8) {
    t10 = <div className={t8} onClick={handleRightClick}>{t9}</div>;
    $[16] = handleRightClick;
    $[17] = t8;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== t10 || $[20] !== t6) {
    t11 = <>{t6}{t10}</>;
    $[19] = t10;
    $[20] = t6;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  return t11;
}
function _temp3(prev_0) {
  return prev_0 + 1;
}
function _temp2(prev) {
  return prev - 1;
}
function CloseButton(t0) {
  const $ = _c(3);
  const {
    onClick
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <IconClose />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== onClick) {
    t2 = <div className="z-navigation fixed top-4 right-4 h-5 w-5 cursor-pointer rounded-full md:absolute md:-top-10 md:-right-7 md:h-7 md:w-7 lg:-right-10" onClick={onClick}>{t1}</div>;
    $[1] = onClick;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
function ThumbnailList(t0) {
  const $ = _c(10);
  const {
    assets,
    selectedAssetIndex,
    setSelectedAssetIndex
  } = t0;
  const containerRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const thumbnailRefs = useRef(t1);
  let t2;
  let t3;
  if ($[1] !== selectedAssetIndex) {
    t2 = () => {
      const selectedThumbnail = thumbnailRefs.current[selectedAssetIndex];
      if (selectedThumbnail) {
        selectedThumbnail.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "center"
        });
      }
    };
    t3 = [selectedAssetIndex];
    $[1] = selectedAssetIndex;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  let t4;
  if ($[4] !== assets || $[5] !== selectedAssetIndex || $[6] !== setSelectedAssetIndex) {
    t4 = assets.map((asset, index) => <div key={index} ref={el => {
      thumbnailRefs.current[index] = el;
    }} className="relative cursor-pointer md:aspect-[100/59] md:min-w-[100px] lg:aspect-[171/100] lg:max-w-[171px] lg:min-w-[171px]" onClick={() => {
      setSelectedAssetIndex(index);
    }}><div className={cn("relative h-full w-full overflow-hidden rounded-[3px] border-transparent transition-all duration-300 md:border-2 lg:rounded-[12px] lg:border-4", {
        "border-[#33C2E9]": selectedAssetIndex === index
      })}>{asset.image?.url || asset.video?.coverImage ? <><OptimizedImage src={asset.image?.url || asset.video.coverImage} className="size-full object-cover md:rounded-[2px] lg:rounded-[9px]" alt="cover" fill={true} sizes="200px" />{asset.image?.url ? <IconPicture className="z-interactive absolute bottom-2 left-2 size-7" /> : <IconVideo className="fill-vive-primary-20 z-interactive absolute bottom-2 left-2 size-7" />}<div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black to-transparent opacity-40" /></> : asset.video?.url && <><video src={asset.video.url} className="size-full object-cover md:rounded-[2px] lg:rounded-[9px]" muted={true} /><IconVideo className="fill-vive-primary-20 z-interactive absolute bottom-2 left-2 size-7" /><div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black to-transparent opacity-40" /></>}</div></div>);
    $[4] = assets;
    $[5] = selectedAssetIndex;
    $[6] = setSelectedAssetIndex;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t4) {
    t5 = <div ref={containerRef} className="no-scrollbar relative mt-6 hidden w-full space-x-6 overflow-x-auto md:flex md:justify-center-safe" onClick={_temp4}>{t4}</div>;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
}
function _temp4(e) {
  return e.stopPropagation();
}
function Lightbox(t0) {
  const $ = _c(38);
  const {
    assets,
    selectedAssetIndex,
    setIsLightboxOpen,
    setSelectedAssetIndex,
    share: t1
  } = t0;
  const share = t1 === undefined ? true : t1;
  const [asset, setAsset] = useState(assets[selectedAssetIndex]);
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef(null);
  let t2;
  if ($[0] !== assets.length || $[1] !== setIsLightboxOpen || $[2] !== setSelectedAssetIndex) {
    t2 = e => {
      if (e.key === "Escape") {
        setIsLightboxOpen(false);
      }
      if (e.key === "ArrowLeft") {
        setSelectedAssetIndex(prev => (prev - 1 + assets.length) % assets.length);
      }
      if (e.key === "ArrowRight") {
        setSelectedAssetIndex(prev_0 => (prev_0 + 1) % assets.length);
      }
    };
    $[0] = assets.length;
    $[1] = setIsLightboxOpen;
    $[2] = setSelectedAssetIndex;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  useEventListener("keydown", t2);
  let t3;
  let t4;
  if ($[4] !== assets || $[5] !== selectedAssetIndex) {
    t3 = () => {
      const newAsset = assets[selectedAssetIndex];
      setAsset(newAsset);
      const video = videoRef.current;
      setIsPlaying(false);
      if (newAsset.video?.url && video) {
        video.pause();
        video.currentTime = 0;
      }
    };
    t4 = [selectedAssetIndex, assets];
    $[4] = assets;
    $[5] = selectedAssetIndex;
    $[6] = t3;
    $[7] = t4;
  } else {
    t3 = $[6];
    t4 = $[7];
  }
  useEffect(t3, t4);
  let t5;
  if ($[8] !== isPlaying) {
    t5 = () => {
      const video_0 = videoRef.current;
      if (video_0) {
        video_0.play();
        setIsPlaying(!isPlaying);
      }
    };
    $[8] = isPlaying;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const handleClickPlay = t5;
  let t6;
  if ($[10] !== setIsLightboxOpen) {
    t6 = () => setIsLightboxOpen(false);
    $[10] = setIsLightboxOpen;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== setIsLightboxOpen) {
    t7 = e_0 => {
      e_0.stopPropagation();
      if (e_0.target === e_0.currentTarget) {
        setIsLightboxOpen(false);
      }
    };
    $[12] = setIsLightboxOpen;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== asset || $[15] !== handleClickPlay || $[16] !== isPlaying || $[17] !== share) {
    t8 = <MainLightboxContent asset={asset} isPlaying={isPlaying} setIsPlaying={setIsPlaying} videoRef={videoRef} handleClickPlay={handleClickPlay} share={share} />;
    $[14] = asset;
    $[15] = handleClickPlay;
    $[16] = isPlaying;
    $[17] = share;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== assets || $[20] !== selectedAssetIndex || $[21] !== setSelectedAssetIndex) {
    t9 = <NavigationButtons assets={assets} selectedAssetIndex={selectedAssetIndex} setSelectedAssetIndex={setSelectedAssetIndex} />;
    $[19] = assets;
    $[20] = selectedAssetIndex;
    $[21] = setSelectedAssetIndex;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== setIsLightboxOpen) {
    t10 = <CloseButton onClick={() => setIsLightboxOpen(false)} />;
    $[23] = setIsLightboxOpen;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== t10 || $[26] !== t7 || $[27] !== t8 || $[28] !== t9) {
    t11 = <div className="relative w-full object-cover md:w-4/5" onClick={t7}>{t8}{t9}{t10}</div>;
    $[25] = t10;
    $[26] = t7;
    $[27] = t8;
    $[28] = t9;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== assets || $[31] !== selectedAssetIndex || $[32] !== setSelectedAssetIndex) {
    t12 = <ThumbnailList assets={assets} selectedAssetIndex={selectedAssetIndex} setSelectedAssetIndex={setSelectedAssetIndex} />;
    $[30] = assets;
    $[31] = selectedAssetIndex;
    $[32] = setSelectedAssetIndex;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  let t13;
  if ($[34] !== t11 || $[35] !== t12 || $[36] !== t6) {
    t13 = <LightboxBackground onClick={t6}>{t11}{t12}</LightboxBackground>;
    $[34] = t11;
    $[35] = t12;
    $[36] = t6;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  return t13;
}
export default Lightbox;